import { SearchApiService } from '../../services/search-api.service';
import { SearchAnalyticsService } from '../../services/search-analytics.service';
import { SearchUtilsService } from '../../services/search-utils.service';
import { ChatConversationService } from '../../services/chat-conversation.service';
import { MessageFormattingService } from '../../services/message-formatting.service';
import { ValidationService } from '../../services/search-validation.service';

const AiAssistant_CONVERSATION_STORAGE_KEY = 'NykreditAIAssistantConversationContext';

function accumulateMessageText(messages, screenReaderSpecificResultCardsHint, screenReaderSpecificGeneralHint) {
    if (!messages || messages.length === 0) {
        return '';
    }

    return `${messages.reduce((acc, message) => {
        if (message.payload) {
            if (message.payload.text) {
                acc += message.payload.text + ' ';
            } else if (message.payload.html) {
                acc += message.payload.html + ' ';
            } else if (message.payload.links) {
                acc += message.payload.links.reduce((linkAcc, link) => `${linkAcc} ${link.type.replaceAll('_', ' ')} ${link.text}`, '');
            }
        }

        if (message.type === 'cards' && message.payload && message.payload.cards) {
            acc += screenReaderSpecificResultCardsHint + message.payload.cards.slice(0, 3).reduce((cardAcc, card) => cardAcc + (card.title ? `anchor link ${card.title} ` : ''), '');
        }

        return acc;
    }, '').trim()} ${screenReaderSpecificGeneralHint}`;
}

export default {
    namespaced: true,
    state: {
        conversationContext: '',
        awaitingResponse: null,
        messages: [],
        screenReaderResponseText: '',
        screenReaderSpecificGeneralHint: '',
        screenReaderSpecificResultCardsHint: '',
        typeWriterToken: null,
        filters: undefined
    },
    mutations: {
        set_aria_labels(state, ariaLabels) {
            state.screenReaderSpecificGeneralHint = ariaLabels.screenReaderSpecificGeneralHint
                || 'Navigér venligst tilbage på siden for at tilgå til alle chatindhold - herunder kort, links og action knapper, og søgeresultater.';
            state.screenReaderSpecificResultCardsHint = ariaLabels.screenReaderSpecificResultCardsHint
                || 'Følgende søgeresultater er fundet: ';
        },
        set_filters(state, filters) {
            state.filters = filters;
        },
        set_conversation_context(state, conversationContext) {
            const typeWriterToken = Date.now();
            state.typeWriterToken = typeWriterToken;
            state.conversationContext = conversationContext;
        },
        set_messages_with_typing_effect(state, messages) {
            if (messages && messages.length > 0) {
                state.screenReaderResponseText = accumulateMessageText(messages, state.screenReaderSpecificResultCardsHint, state.screenReaderSpecificGeneralHint);

                const lastMessage =
                    (messages[messages.length - 1].type === 'cards' && messages.length > 1) ?
                        messages[messages.length - 2]
                        : messages[messages.length - 1];

                if (lastMessage) {
                    lastMessage.lastMessageInGroup = true;
                    if (MessageFormattingService.isAiGeneratedMessage(messages)) {
                        lastMessage.aiGenerated = true;
                    }
                }
            }

            MessageFormattingService.streamMessagesWithTypeWriterEffect(state, [...messages, { type: 'separator' }], state.typeWriterToken);
        },
        reset_typewriter(state) {
            state.typeWriterToken = Date.now();
        },
        set_messages(state, message) {
            state.messages = [...state.messages, message];
        },
        set_awaiting_response(state, actionType) {
            state.awaitingResponse = actionType;
        },
        set_clear_messages(state) {
            state.messages = [];
        },
        reset_conversational_state(state) {
            const resetState = SearchUtilsService.resetConversationalState();
            Object.assign(state, resetState);
        }
    },
    actions: {
        clearMessages(context) {
            context.commit('set_clear_messages');
        },
        async performSearch(context, payload) {
            const validation = ValidationService.validateSearchQuery(payload.message);
            if (!validation.isValid) {
                console.error('Invalid query:', validation.error);
                return;
            }

            if (!payload.enableSearch || ValidationService.shouldSkipSearch(payload.message, payload.searchKeywordBlacklist)) {
                payload.messages[payload.messages.length - 1].lastMessageInGroup = true;
                context.commit('set_messages_with_typing_effect', payload.messages);
                context.commit('set_awaiting_response', null);
                return;
            }

            try {
                const searchConfig = {
                    apiUrl: payload.apiUrl,
                    query: payload.message,
                    lang: payload.lang,
                    page: payload.page,
                    activeFilter: context.state.activeFilter
                };

                const json = await SearchApiService.performSearch(searchConfig);

                SearchAnalyticsService.trackSearch(payload.message, json.unfilteredNumberOfHits, 'ai-assistant');

                const hitsMessage = MessageFormattingService.createMessageCards(json.hits);

                context.commit('set_messages_with_typing_effect', [
                    ...(payload.messages || []),
                    hitsMessage
                ]);
            } catch (error) {
                console.error('Search failed:', error);
            } finally {
                context.commit('set_awaiting_response', null);
            }
        },
        async start(context, payload) {
            const conversationContext = ChatConversationService.getStoredConversation(AiAssistant_CONVERSATION_STORAGE_KEY);
            context.commit('set_aria_labels', payload.ariaLabels);
            context.commit('set_filters', payload.filters);

            if (!payload.isNewSearch && conversationContext) {
                context.commit('set_conversation_context', conversationContext);
                context.dispatch('submitInquiry', payload);
                return;
            }

            try {
                const json = await ChatConversationService.startConversationWithActionId(
                    AiAssistant_CONVERSATION_STORAGE_KEY,
                    payload.introActionId,
                    context.state.filters
                );
                context.commit('set_conversation_context', json);
                context.dispatch('submitInquiry', payload);
            } catch (error) {
                console.error('Failed to start conversation:', error);
            }
        },
        async submitInquiry(context, { message, lang, apiUrl, enableSearch, searchKeywordBlacklist }) {
            if (!message) return;
            context.commit('reset_typewriter');
            context.commit('set_messages',
                MessageFormattingService.createMessageObject({
                    text: message,
                    type: 'text',
                    isScrollAnchor: true,
                    source: 'user'
                })
            );

            try {
                context.commit('set_awaiting_response', 'message');
                const json = await ChatConversationService.sendMessage(
                    context.state.conversationContext.conversation.id,
                    message,
                    context.state.filters
                );
                context.dispatch('performSearch', {
                    message,
                    lang,
                    enableSearch,
                    searchKeywordBlacklist,
                    apiUrl,
                    messages: MessageFormattingService.setMessagesMeta(json.response).elements,
                    page: 1,
                });
            } catch (error) {
                console.error("Unable to send message:", error);
                context.commit('set_awaiting_response', null);
            }
        },
        async handleActionIdFromQueryParam(context, { ariaLabels, filters }) {
            const urlParams = new URLSearchParams(window.location.search);
            const actionId = urlParams.get('action-id');

            if (!actionId) return;

            context.commit('set_aria_labels', ariaLabels);
            context.commit('set_filters', filters);

            const conversationContext = ChatConversationService.getStoredConversation(AiAssistant_CONVERSATION_STORAGE_KEY);

            try {
                if (conversationContext) {
                    // Resume existing conversation and send the action
                    context.commit('set_conversation_context', conversationContext);
                    await context.dispatch('sendActionId', { actionId, isFloatingTrigger: true });
                } else {
                    // Start a fresh conversation kicked off by the action ID
                    context.commit('reset_typewriter');
                    context.commit('set_awaiting_response', 'send-action');
                    const json = await ChatConversationService.startConversationWithActionId(
                        AiAssistant_CONVERSATION_STORAGE_KEY,
                        actionId,
                        context.state.filters
                    );
                    context.commit('set_conversation_context', json);
                    if (json.response) {
                        context.commit('set_messages_with_typing_effect', MessageFormattingService.setMessagesMeta(json.response, { setLastMessageInGroup: true }).elements);
                    }
                }
            } catch (error) {
                console.error('Failed to handle action ID from query param:', error);
            } finally {
                context.commit('set_awaiting_response', null);
            }
        },
        async sendActionId(context, { actionId, isFloatingTrigger }) {
            try {
                context.commit('reset_typewriter');
                context.commit('set_awaiting_response', 'send-action');
                const json = await ChatConversationService.sendActionId(
                    context.state.conversationContext.conversation.id,
                    actionId,
                    isFloatingTrigger,
                    context.state.filters
                );
                if (!json || !json.response || !json.response.elements) {
                    throw new Error("Invalid response from sendActionId");
                }
                context.commit('set_messages_with_typing_effect', MessageFormattingService.setMessagesMeta(json.response, { setLastMessageInGroup: true }).elements);
            } catch (error) {
                console.error("Unable to send action:", error);
            } finally {
                context.commit('set_awaiting_response', null);
            }
        },
        async sendExternalLink({ state }, payload = {}) {
            try {
                await ChatConversationService.sendExternalLink(
                    state.conversationContext.conversation.id,
                    payload,
                    state.filters
                );
            } catch (err) {
                throw new Error("Unable to send external link: " + err);
            }
        },
        async sendMessageFeedback({ state }, payload) {
            const { feedback, id } = payload;

            try {
                await ChatConversationService.sendMessageFeedback(state.conversationContext.conversation.id, feedback, id, state.filters);
                return true;
            } catch (err) {
                throw new Error("Unable to send feedback: " + err);
            }
        }
    },
    getters: {
        screenReaderResponseText(state) {
            return state.screenReaderResponseText;
        }
    }
}